import { Route as RouteIcon } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import { Card, CardBody, CardHeader, CardTitle } from '@/components/ui/Card'
import { useFormat } from '@/i18n/useFormat'
import { cn } from '@/lib/cn'
import { placeName } from '@/lib/countries'
import { modeLabelKey } from '@/lib/icons'
import type { CaseDetail, RouteStop, RouteStopRole, RouteStopState } from '@/types/domain'

const STOP_DOT: Record<RouteStopState, string> = {
  passed: 'bg-positive',
  current: 'bg-attention',
  ahead: 'border-neutral-300 bg-surface border-[1.5px]',
}

/** The places the desk typed on the case, in route order, for a case the
 *  server sends no stop list for — which is every real case today: `route`
 *  is served by the showcase fixture only, and the overview said «Маршрут ещё
 *  не задан» under a header reading Shanghai → Almaty (owner's live test,
 *  2026-10-17). An unloading place that repeats the discharge point is the
 *  same place said twice, and is printed once. A converted case stores the
 *  places as the ISO codes the quote carried (`TM`, `RU`), so each is named in
 *  the reader's language here, like the case header's own route (#136). */
function typedPlaces(
  record: CaseDetail,
  language: string,
): { role: RouteStopRole; name: string }[] {
  const same = (a?: string, b?: string) =>
    (a ?? '').trim().toLocaleLowerCase() === (b ?? '').trim().toLocaleLowerCase()
  const places: [RouteStopRole, string | undefined][] = [
    ['loading', record.placeOfLoading],
    ['border', record.borderCrossing],
    ['discharge', record.placeOfDischarge],
    [
      'unloading',
      same(record.unloadingPlace, record.placeOfDischarge) ? undefined : record.unloadingPlace,
    ],
  ]
  return places.flatMap(([role, name]) => {
    const printed = placeName(name, language)
    return printed ? [{ role, name: printed }] : []
  })
}

/** The case route across the top: POL, border crossing, POD, unloading place,
 *  each captioned with what it is and where the shipment stands against it.
 *  The state words come from the SERVER — "here now" is never re-derived here
 *  from leg statuses, so the route and the stuck flag cannot disagree. Colour
 *  on the dots restates the caption, never replaces it. */
export function RouteCard({ record }: { record: CaseDetail }) {
  const { t, i18n } = useTranslation()
  const format = useFormat()
  const stops = record.route ?? []
  /* Only when there is no stop list: the served stops carry states and dates,
     the typed places carry neither, and the two are never mixed. */
  const places = stops.length === 0 ? typedPlaces(record, i18n.language) : []

  /* "rail + road via Khorgos" — the modes in leg order, deduplicated, and the
     border stop by name. Derived rather than served: it restates facts already
     in this response, in one breath.

     Before any leg is planned the case's own mode stands in, and that may be
     ABSENT (a NULL column): the line is then a dash, never a guessed mode. */
  const modes = record.legs.length
    ? [...new Set(record.legs.map((leg) => leg.mode))]
    : record.mode
      ? [record.mode]
      : []
  const modeLine = modes
    .map((mode) => t(modeLabelKey(mode)).toLocaleLowerCase())
    .join(' + ')
  const border =
    stops.find((stop) => stop.role === 'border') ??
    places.find((place) => place.role === 'border')

  const status = (stop: RouteStop): string => {
    switch (stop.state) {
      case 'passed':
        /* The origin was LEFT, every later passed stop was reached — two
           different facts, two different words. */
        return stop.role === 'loading'
          ? t('cases.overview.departed', { date: format.dateShort(stop.date) })
          : t('cases.overview.arrivedOn', { date: format.dateShort(stop.date) })
      case 'current':
        return stop.dwellDays != null
          ? t('cases.overview.hereNowFor', { days: stop.dwellDays })
          : t('cases.overview.hereNow')
      case 'ahead':
        return t('cases.overview.etaOn', { date: format.dateShort(stop.date) })
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
          <span className="flex items-center gap-2">
            <RouteIcon size={15} className="text-text-muted" aria-hidden />
            {t('cases.overview.route')}
          </span>
          <span className="text-text-muted text-[12.5px] font-light">
            {!modeLine
              ? '—'
              : border
                ? t('cases.overview.routeVia', { modes: modeLine, border: border.name })
                : modeLine}
          </span>
        </CardTitle>
      </CardHeader>
      <CardBody className="pt-3 pb-4">
        {/* A case with no stops yet still holds its place in the overview's
            grid, and says so rather than standing empty. */}
        {stops.length === 0 && places.length === 0 ? (
          <p className="text-text-muted text-[13px] font-light">{t('cases.overview.routeEmpty')}</p>
        ) : null}
        <div className="flex flex-wrap justify-between gap-x-8 gap-y-3">
          {stops.map((stop) => (
            <div key={stop.id} className="flex min-w-0 flex-col gap-1.5">
              <span className="flex items-center gap-2">
                <span
                  aria-hidden
                  className={cn('size-2 shrink-0 rounded-full', STOP_DOT[stop.state])}
                />
                <span className="text-text-heading text-[13px] leading-none font-medium">
                  {stop.name}
                </span>
              </span>
              {/* max-w keeps a long caption wrapping under its own stop rather
                  than pushing the row apart; pl-4 hangs it under the name, not
                  the dot. */}
              <span
                className={cn(
                  'max-w-44 pl-4 text-[12px] leading-snug font-light',
                  stop.state === 'current' ? 'text-attention' : 'text-text-muted',
                )}
              >
                {t(`cases.overview.stopRole.${stop.role}`)} · {status(stop)}
              </span>
            </div>
          ))}
          {/* A typed place has no state to report, so its dot is the neutral
              ring and its caption is the role alone — never a guessed date. */}
          {places.map((place) => (
            <div key={place.role} className="flex min-w-0 flex-col gap-1.5">
              <span className="flex items-center gap-2">
                <span aria-hidden className={cn('size-2 shrink-0 rounded-full', STOP_DOT.ahead)} />
                <span className="text-text-heading text-[13px] leading-none font-medium">
                  {place.name}
                </span>
              </span>
              <span className="text-text-muted max-w-44 pl-4 text-[12px] leading-snug font-light">
                {t(`cases.overview.stopRole.${place.role}`)}
              </span>
            </div>
          ))}
        </div>
      </CardBody>
    </Card>
  )
}
